import { Ionicons } from "@expo/vector-icons";
import { ParamListBase, type NavigationProp, useNavigation } from "@react-navigation/native";
import React from "react";
import { View, Switch, TouchableOpacity, StyleSheet, Platform, Alert, Linking } from "react-native";
import tw from "twrnc";

import LanguagePicker from "../../components/LanguagePicker";
import ScreenWrapper from "../../components/ScreenWrapper";
import { DSText } from "../../design-system/components/DSText";
import { useAppTheme } from "../../design-system/ThemeProvider";
import { radius, shadows, spacing } from "../../design-system/tokens";
import { useLanguageStore } from "../../features/quiz-core/languageStore";
import { t } from "../../i18n/translations";
import { supabase } from "../../lib/supabase";
import { useGuestUser } from "../contexts/GuestUserContext";
import { authService } from "../services/authService";
import { loadAllSettings, saveSetting } from "../storage/settingsStorage";

type ThemeColors = ReturnType<typeof useAppTheme>["colors"];

const shellColors = {
  canvas: "#120A2B",
  panel: "#080B24",
  panelSoft: "rgba(14, 18, 48, 0.92)",
  gold: "#F8C84E",
  goldSoft: "rgba(248, 200, 78, 0.18)",
  purple: "#6D35A8",
  ivory: "#FFF7DA",
  muted: "#D9D2C2",
  line: "rgba(248, 200, 78, 0.38)",
};

interface SettingsRowProps {
  colors: ThemeColors;
  icon: React.ComponentProps<typeof Ionicons>["name"];
  iconBackground: string;
  label: string;
  detail?: string;
  onPress?: () => void;
  trailing?: React.ReactNode;
  danger?: boolean;
}

const SettingsRow = ({
  colors,
  icon,
  iconBackground,
  label,
  detail,
  onPress,
  trailing,
  danger = false,
}: SettingsRowProps) => (
  <TouchableOpacity
    disabled={!onPress}
    onPress={onPress}
    style={{
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: spacing.lg,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: shellColors.line,
    }}
  >
    <View
      style={{
        borderRadius: radius.md,
        padding: spacing.sm,
        marginRight: spacing.lg,
        backgroundColor: iconBackground,
        ...shadows.sm,
      }}
    >
      <Ionicons name={icon} size={18} color={colors.white} />
    </View>
    <View style={{ flex: 1, paddingRight: spacing.md }}>
      <DSText variant="label" color={danger ? colors.incorrectDark : shellColors.ivory} style={{ marginBottom: spacing.xs }}>
        {label}
      </DSText>
      {!!detail && (
        <DSText variant="caption" color={shellColors.muted}>
          {detail}
        </DSText>
      )}
    </View>
    {trailing || (onPress ? <Ionicons name="chevron-forward" size={18} color={shellColors.gold} /> : null)}
  </TouchableOpacity>
);

const SettingsSection = ({
  colors: _colors,
  title,
  subtitle,
  children,
}: {
  colors: ThemeColors;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <View
    style={[
      {
        borderRadius: radius.xl,
        padding: spacing["2xl"],
        marginBottom: spacing["2xl"],
        backgroundColor: shellColors.panelSoft,
        borderWidth: 1,
        borderColor: shellColors.line,
      },
      shadows.md,
    ]}
  >
    <DSText variant="micro" uppercase color={shellColors.gold} style={{ marginBottom: spacing.sm, fontWeight: "700", letterSpacing: 1 }}>
      {title}
    </DSText>
    {!!subtitle && (
      <DSText variant="caption" color={shellColors.muted} style={{ marginBottom: spacing.md }}>
        {subtitle}
      </DSText>
    )}
    {children}
  </View>
);

const SettingsScreen = () => {
  const { language } = useLanguageStore();
  const { isGuest, endGuestSession } = useGuestUser();
  const { colors, isDark, toggleDarkMode } = useAppTheme();
  const navigation = useNavigation<NavigationProp<ParamListBase>>();

  const [notificationsEnabled, setNotificationsEnabled] = React.useState(true);
  const [soundEnabled, setSoundEnabled] = React.useState(false);
  const [settingsLanguage, setSettingsLanguage] = React.useState(language || "en");
  const setGlobalLanguage = useLanguageStore((s) => s.setLanguage);

  const handleLanguageChange = (code: string) => {
    setSettingsLanguage(code);
    setGlobalLanguage(code);
  };

  React.useEffect(() => {
    if (language && language !== settingsLanguage) {
      setSettingsLanguage(language);
    }
  }, [language, settingsLanguage]);

  React.useEffect(() => {
    (async () => {
      const all = await loadAllSettings();
      if (all.notificationsEnabled !== undefined) setNotificationsEnabled(all.notificationsEnabled);
      if (all.soundEnabled !== undefined) setSoundEnabled(all.soundEnabled);
      if (all.language !== undefined && all.language) setSettingsLanguage(all.language);
    })();
  }, [language]);

  React.useEffect(() => { saveSetting("notificationsEnabled", notificationsEnabled); }, [notificationsEnabled]);
  React.useEffect(() => { saveSetting("soundEnabled", soundEnabled); }, [soundEnabled]);
  React.useEffect(() => { saveSetting("language", settingsLanguage); }, [settingsLanguage]);

  const [signingOut, setSigningOut] = React.useState(false);
  const [deletingAccount, setDeletingAccount] = React.useState(false);
  const [userEmail, setUserEmail] = React.useState<string | null>(null);
  const [authProvider, setAuthProvider] = React.useState<string | null>(null);

  // Load current user info for reset password / delete account
  React.useEffect(() => {
    if (isGuest) return;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setUserEmail(user.email || null);
        setAuthProvider(user.app_metadata?.provider || 'email');
      }
    });
  }, [isGuest]);

  const handleResetPassword = async () => {
    if (!userEmail) {
      Alert.alert(
        t("settings.error", language) || "Error",
        t("settings.noEmailFound", language) || "No email address found for this account."
      );
      return;
    }

    if (authProvider === 'google' || authProvider === 'apple') {
      Alert.alert(
        t("settings.resetPasswordTitle", language) || "Reset Password",
        t("settings.oauthNoPassword", language) ||
          `You signed in with ${authProvider === 'google' ? 'Google' : 'Apple'}. Password reset is not needed — just sign in with your ${authProvider === 'google' ? 'Google' : 'Apple'} account.`,
        [{ text: t("settings.ok", language) || "OK" }]
      );
      return;
    }

    Alert.alert(
      t("settings.resetPasswordTitle", language) || "Reset Password",
      (t("settings.resetPasswordMessage", language) || "We'll send a password reset link to {email}").replace("{email}", userEmail),
      [
        { text: t("coreUI.cancel", language) || "Cancel", style: "cancel" },
        {
          text: t("settings.sendResetLink", language) || "Send Reset Link",
          onPress: async () => {
            try {
              await authService.resetPassword(userEmail);
              Alert.alert(
                t("settings.resetPasswordSent", language) || "Email Sent",
                (t("settings.resetPasswordSentMessage", language) || "Check your inbox at {email} for a password reset link.").replace("{email}", userEmail),
                [{ text: t("settings.ok", language) || "OK" }]
              );
            } catch {
              Alert.alert(
                t("settings.error", language) || "Error",
                t("settings.resetPasswordFailed", language) || "Failed to send reset email. Please try again."
              );
            }
          },
        },
      ]
    );
  };

  const handleDeleteAccount = async () => {
    const providerLabel = authProvider === 'google' ? 'Google' : authProvider === 'apple' ? 'Apple' : 'email';

    Alert.alert(
      t("moreScreen.deleteAccountTitle", language) || "Delete Account",
      t("moreScreen.deleteAccountMessage", language) ||
        "This will permanently delete your account and all associated data. This action cannot be undone.",
      [
        { text: t("coreUI.cancel", language) || "Cancel", style: "cancel" },
        {
          text: t("moreScreen.deleteAccount", language) || "Delete Account",
          style: "destructive",
          onPress: () => {
            // Second confirmation
            Alert.alert(
              t("settings.deleteConfirmTitle", language) || "Are you sure?",
              (t("settings.deleteConfirmMessage", language) || "Your {provider} account ({email}) and all quiz data will be permanently deleted.")
                .replace("{provider}", providerLabel)
                .replace("{email}", userEmail || ""),
              [
                { text: t("coreUI.cancel", language) || "Cancel", style: "cancel" },
                {
                  text: t("settings.deleteConfirmButton", language) || "Yes, Delete Everything",
                  style: "destructive",
                  onPress: async () => {
                    setDeletingAccount(true);
                    try {
                      await authService.deleteAccount("user_requested");
                      Alert.alert(
                        t("moreScreen.accountDeleted", language) || "Account Deleted",
                        t("moreScreen.accountDeletedMessage", language) || "Your account has been permanently deleted."
                      );
                    } catch (error: unknown) {
                      const message = error instanceof Error ? error.message : "";
                      if (message === "Re-authentication required") {
                        Alert.alert(
                          t("settings.reAuthRequired", language) || "Verify Identity",
                          t("settings.reAuthMessage", language) || "Please sign out and sign back in, then try again.",
                          [{ text: t("settings.ok", language) || "OK" }]
                        );
                      } else {
                        Alert.alert(
                          t("settings.error", language) || "Error",
                          t("moreScreen.failedDeleteAccount", language) || "Failed to delete account. Please try again."
                        );
                      }
                    } finally {
                      setDeletingAccount(false);
                    }
                  },
                },
              ]
            );
          },
        },
      ]
    );
  };

  const handleSignOut = async () => {
    if (isGuest) {
      const message =
        t("settings.guestNoAccount", language) ||
        "You are playing as a guest. Sign in to create an account and save your progress.";
      if (Platform.OS === "web") {
        alert(message);
      } else {
        Alert.alert(
          t("settings.notSignedIn", language) || "Not Signed In",
          message,
          [
            { text: t("settings.ok", language) || "OK", style: "cancel" },
            { text: t("auth.signIn", language) || "Sign In", onPress: () => endGuestSession() },
          ]
        );
      }
      return;
    }

    if (signingOut) return; // Prevent double-tap
    setSigningOut(true);

    try {
      await authService.signOut();

      if (Platform.OS === "web" && typeof window !== "undefined") {
        setTimeout(() => {
          window.location.href = "/";
        }, 100);
      }
    } catch {
      setSigningOut(false);
      alert(t("settings.signOutFailed", language));
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: shellColors.canvas }}>
      <ScreenWrapper contentContainerStyle={styles.container}>
        <View
          style={[
            {
              borderRadius: radius.xl,
              padding: spacing["3xl"],
              marginBottom: spacing["2xl"],
              backgroundColor: shellColors.panel,
              borderWidth: 1,
              borderColor: shellColors.line,
            },
            shadows.lg,
          ]}
        >
          <View style={tw`flex-row items-center justify-between`}>
            <View style={{ flex: 1, paddingRight: spacing.lg }}>
              <DSText variant="micro" uppercase color={shellColors.gold} style={{ marginBottom: spacing.sm, fontWeight: "700", letterSpacing: 1 }}>
                {t("settings.title", language)}
              </DSText>
              <DSText variant="heading" color={shellColors.ivory} style={{ marginBottom: spacing.sm }}>
                {t("settings.title", language)}
              </DSText>
              <DSText variant="caption" color={shellColors.muted}>
                {isGuest
                  ? (t("settings.guestNoAccount", language) || "Sign in to keep your progress synced across devices.")
                  : (t("quickActions.settingsSubtitle", language) || "App preferences")}
              </DSText>
            </View>
            <View
              style={{
                borderRadius: radius.lg,
                padding: spacing.lg,
                backgroundColor: shellColors.goldSoft,
                borderWidth: 1,
                borderColor: shellColors.line,
                ...shadows.md,
              }}
            >
              <Ionicons name="settings" size={28} color={shellColors.gold} />
            </View>
          </View>
        </View>

        <SettingsSection
          colors={colors}
          title={t("moreScreen.profile", language) || "Profile"}
        >
          <SettingsRow
            colors={colors}
            icon="person"
            iconBackground={colors.correct}
            label={t("moreScreen.profile", language) || "Profile"}
            detail={t("moreScreen.profileSubtitle", language) || "View and edit your profile"}
            onPress={() => navigation.navigate("ProfileDetail")}
          />
        </SettingsSection>

        {/* Premium / Subscription — always visible for signed-in users */}
        {!isGuest && (
          <SettingsSection
            colors={colors}
            title={t("settings.subscription", language) || "Subscription"}
          >
            <SettingsRow
              colors={colors}
              icon="diamond"
              iconBackground="#7C3AED"
              label={t("settings.premium", language) || "Premium"}
              detail={t("settings.managePremium", language) || "View plans and manage your subscription"}
              onPress={() => navigation.navigate("SubscriptionPlans" as never)}
            />
          </SettingsSection>
        )}

        <SettingsSection
          colors={colors}
          title={t("settings.title", language) || "Settings"}
        >
          <SettingsRow
            colors={colors}
            icon="notifications"
            iconBackground={colors.accent}
            label={t("settings.notifications", language)}
            trailing={
              <Switch
                value={notificationsEnabled}
                onValueChange={setNotificationsEnabled}
                thumbColor={notificationsEnabled ? colors.correct : colors.surfaceContainerHighest}
                trackColor={{ false: colors.outlineVariant, true: colors.correctLight }}
              />
            }
          />

          <SettingsRow
            colors={colors}
            icon="volume-high"
            iconBackground="#8B5CF6"
            label={t("settings.sound", language)}
            trailing={
              <Switch
                value={soundEnabled}
                onValueChange={setSoundEnabled}
                thumbColor={soundEnabled ? colors.correct : colors.surfaceContainerHighest}
                trackColor={{ false: colors.outlineVariant, true: colors.correctLight }}
              />
            }
          />

          <SettingsRow
            colors={colors}
            icon="moon"
            iconBackground={colors.primaryLight}
            label={t("settings.darkMode", language)}
            trailing={
              <Switch
                value={isDark}
                onValueChange={toggleDarkMode}
                thumbColor={isDark ? colors.correct : colors.surfaceContainerHighest}
                trackColor={{ false: colors.outlineVariant, true: colors.correctLight }}
              />
            }
          />

          <SettingsRow
            colors={colors}
            icon="language"
            iconBackground={colors.incorrect}
            label={t("settings.language", language)}
            trailing={
              <LanguagePicker
                value={settingsLanguage}
                onChange={handleLanguageChange}
                style={{
                  flex: 0,
                  minWidth: 140,
                  backgroundColor: shellColors.panelSoft,
                  borderColor: shellColors.line,
                }}
                textColor={shellColors.ivory}
              />
            }
          />
        </SettingsSection>

        <SettingsSection
          colors={colors}
          title={t("about.appInformation", language) || "Support"}
          subtitle={t("about.description", language) || "Important links and product details."}
        >
          <SettingsRow
            colors={colors}
            icon="document-text"
            iconBackground={colors.correct}
            label={t("settings.privacyPolicy", language)}
            onPress={() => Linking.openURL("https://kingdombiblequiz.com/privacy")}
          />

          <SettingsRow
            colors={colors}
            icon="shield-checkmark"
            iconBackground="#8B5CF6"
            label={t("settings.termsOfService", language)}
            onPress={() => Linking.openURL("https://kingdombiblequiz.com/terms")}
          />

          <SettingsRow
            colors={colors}
            icon="information-circle"
            iconBackground="#06B6D4"
            label={t("settings.about", language)}
            detail={t("about.tagline", language) || "Learn more about Kingdom Bible Quiz"}
            onPress={() => navigation.navigate("About")}
          />
        </SettingsSection>

        {/* Account Management — only for signed-in users */}
        {!isGuest && (
          <SettingsSection
            colors={colors}
            title={t("settings.accountManagement", language) || "Account"}
          >
            {/* Reset Password — shown for all users, explains OAuth path */}
            <SettingsRow
              colors={colors}
              icon="key"
              iconBackground="#F59E0B"
              label={t("settings.resetPasswordTitle", language) || "Reset Password"}
              detail={authProvider === 'google' || authProvider === 'apple'
                ? (t("settings.signedInWith", language) || "Signed in with {provider}").replace("{provider}", authProvider === 'google' ? 'Google' : 'Apple')
                : (t("settings.resetPasswordDetail", language) || "Send a reset link to your email")}
              onPress={handleResetPassword}
            />

            {/* Delete Account */}
            <SettingsRow
              colors={colors}
              icon="trash"
              iconBackground="#DC2626"
              label={deletingAccount
                ? (t("settings.deletingAccount", language) || "Deleting...")
                : (t("moreScreen.deleteAccount", language) || "Delete Account")}
              detail={t("moreScreen.deleteAccountDescription", language) || "Permanently delete your account and all data"}
              onPress={handleDeleteAccount}
              danger
            />
          </SettingsSection>
        )}

        <SettingsSection
          colors={colors}
          title={t("settings.signOut", language) || "Account actions"}
          subtitle={isGuest
            ? (t("quickActions.guestNoAccount", language) || "You are currently playing as a guest.")
            : undefined}
        >
          <SettingsRow
            colors={colors}
            icon="log-out"
            iconBackground={colors.incorrect}
            label={signingOut
              ? (t("settings.signingOut", language) || "Signing out...")
              : t("settings.signOut", language)}
            detail={isGuest
              ? (t("auth.signIn", language) || "Sign in")
              : (t("settings.endSession", language) || "End your current session")}
            onPress={handleSignOut}
            danger={!isGuest}
          />
        </SettingsSection>
      </ScreenWrapper>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: spacing["2xl"],
  },
});

export default SettingsScreen;
